/**
 * Dictation: speech to text into the message box, free, with no server of
 * ours in the path. The listening is done by what the page runs in:
 *
 * - In the packaged app, the shell's `ArkSpeech` plugin
 *   (client-app/capacitor/android, ArkSpeechPlugin.java) runs Android's own
 *   SpeechRecognizer. The WebView has no speech recognition of its own.
 * - In a browser, the Web Speech API (`SpeechRecognition`, prefixed
 *   `webkitSpeechRecognition` in Chrome and Safari). Firefox has none, and a
 *   page on plain http (a box reached by its LAN address) is refused the
 *   microphone, so there is no dictation there and the mic is not shown.
 *
 * Both hand back what they hear as they hear it, and both stop by themselves
 * after a pause; either way the words are the device's, the audio never
 * reaches the box.
 */

import { useSyncExternalStore } from "react";

import { text } from "./i18n.js";
import { DICTATION } from "./text/dictation.js";

/** Why a session ended early, for the one line the composer shows. */
export type DictationProblem = "denied" | "no-speech" | "no-mic" | "network" | "language" | "failed";

export interface DictationHandlers {
  /** Everything heard so far in this session: what is settled, then what is still being guessed. */
  onHeard(heard: { readonly settled: string; readonly guess: string }): void;
  /** The session is over; called once. `null` when it ended as asked, or after a pause. */
  onEnd(problem: DictationProblem | null): void;
}

export interface DictationSession {
  /** Finish, keeping what was heard: the last guess settles before onEnd. */
  stop(): void;
  /** Finish, dropping anything not yet settled. */
  cancel(): void;
}

export interface DictationOptions {
  readonly language: string;
}

export interface Dictation {
  readonly kind: "shell" | "browser";
  /**
   * Whether a session hears on through pauses until it is stopped. Where it
   * does not (the app's recogniser, Chrome on Android), the device decides
   * when the speaker has finished, and a session's own end is that finish:
   * auto-send sends then, and its wait does not apply. Starting a new session
   * straight after Android's recogniser ends fails there as often as not.
   */
  readonly listensThroughPauses: boolean;
  start(options: DictationOptions, handlers: DictationHandlers): DictationSession;
}

/** The languages offered, in the order the menus list them. */
export const DICTATION_LANGUAGES = [
  { tag: "id-ID", label: "ID", name: "Bahasa Indonesia" },
  { tag: "en-US", label: "EN", name: "English" },
  { tag: "ja-JP", label: "JP", name: "日本語" },
] as const;

export type DictationLanguage = (typeof DICTATION_LANGUAGES)[number]["tag"];

export function dictationLanguageInfo(language: DictationLanguage): (typeof DICTATION_LANGUAGES)[number] {
  return DICTATION_LANGUAGES.find((entry) => entry.tag === language) ?? DICTATION_LANGUAGES[0];
}

/**
 * This device's dictation choices, made in Settings → General or from the
 * listening bar, kept in localStorage as the theme is (theme.ts): the
 * language listened for (Indonesian until another is picked), whether a
 * message dictated is sent once the speaker stops (off until turned on), and
 * how long a silence counts as stopping (three seconds until changed, and
 * only in Settings).
 */
export interface DictationSettings {
  readonly language: DictationLanguage;
  readonly autoSend: boolean;
  /** Milliseconds with no new words, after some were heard, before auto-send sends; see `isAutoSendDelay`. */
  readonly autoSendDelay: number;
}

/** The silences Settings' slider offers: half a second to five, by half seconds. */
export const AUTO_SEND_DELAY_MIN = 500;
export const AUTO_SEND_DELAY_MAX = 5000;
export const AUTO_SEND_DELAY_STEP = 500;
export const DEFAULT_AUTO_SEND_DELAY = 3000;

export function isAutoSendDelay(ms: number): boolean {
  return Number.isInteger(ms) && ms >= AUTO_SEND_DELAY_MIN && ms <= AUTO_SEND_DELAY_MAX && ms % AUTO_SEND_DELAY_STEP === 0;
}

const LANGUAGE_KEY = "ark-bot.dictation.language";
const AUTO_SEND_KEY = "ark-bot.dictation.autoSend";
const AUTO_SEND_DELAY_KEY = "ark-bot.dictation.autoSendDelay";

export function readDictationSettings(storage: Pick<Storage, "getItem"> | null | undefined): DictationSettings {
  const read = (key: string): string | null => {
    try {
      return storage?.getItem(key) ?? null;
    } catch {
      return null;
    }
  };
  const saved = read(LANGUAGE_KEY);
  const language = DICTATION_LANGUAGES.find((entry) => entry.tag === saved)?.tag ?? "id-ID";
  const savedDelay = read(AUTO_SEND_DELAY_KEY);
  const delay = savedDelay == null || savedDelay === "" ? NaN : Number(savedDelay);
  const autoSendDelay = isAutoSendDelay(delay) ? delay : DEFAULT_AUTO_SEND_DELAY;
  return { language, autoSend: read(AUTO_SEND_KEY) === "on", autoSendDelay };
}

function browserStorage(): Storage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

const listeners = new Set<() => void>();
let settings: DictationSettings = readDictationSettings(browserStorage());

function save(key: string, value: string, next: DictationSettings): void {
  try {
    browserStorage()?.setItem(key, value);
  } catch {
    // Storage refused (a private window): the choice holds until the page is closed.
  }
  settings = next;
  for (const listener of listeners) listener();
}

export function setDictationLanguage(language: DictationLanguage): void {
  save(LANGUAGE_KEY, language, { ...settings, language });
}

export function setDictationAutoSend(autoSend: boolean): void {
  save(AUTO_SEND_KEY, autoSend ? "on" : "off", { ...settings, autoSend });
}

export function setDictationAutoSendDelay(autoSendDelay: number): void {
  if (!isAutoSendDelay(autoSendDelay)) return;
  save(AUTO_SEND_DELAY_KEY, String(autoSendDelay), { ...settings, autoSendDelay });
}

export function dictationSettings(): DictationSettings {
  return settings;
}

export function subscribeDictationSettings(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useDictationSettings(): DictationSettings {
  return useSyncExternalStore(subscribeDictationSettings, dictationSettings);
}

/** For tests: read the choices again from the storage given. */
export function reloadDictationSettings(storage: Pick<Storage, "getItem"> | null): void {
  settings = readDictationSettings(storage);
}

/**
 * Words heard, put in the field where the caret was when listening began:
 * a space is added on either side only where the text there does not
 * already have one. The result is the whole field, the caret after the
 * words, and the edit, so the mention blocks after them can move along.
 */
export function placeHeard(before: string, after: string, heard: string): { text: string; caret: number; at: number; inserted: number } {
  const words = heard.trim().replace(/\s+/g, " ");
  if (words.length === 0) return { text: before + after, caret: before.length, at: before.length, inserted: 0 };
  const lead = before.length > 0 && !/\s$/.test(before) ? " " : "";
  const trail = after.length > 0 && !/^\s/.test(after) ? " " : "";
  const inserted = `${lead}${words}${trail}`;
  return { text: before + inserted + after, caret: before.length + lead.length + words.length, at: before.length, inserted: inserted.length };
}

/** Pieces of speech joined as one run of words: each engine gives them with and without their own spaces. */
function join(pieces: readonly string[]): string {
  return pieces
    .map((piece) => piece.trim())
    .filter((piece) => piece.length > 0)
    .join(" ");
}

// --- the shell: Android's SpeechRecognizer, through the ArkSpeech plugin ---

interface Removable {
  remove(): Promise<void> | void;
}

/** What the plugin offers, as the shell's generated proxy exposes it (push.ts says how that proxy comes to be). */
export interface ShellSpeech {
  available(): Promise<{ readonly available: boolean }>;
  /** Asks for the microphone the first time; rejects with code "denied" when refused. */
  start(options: { readonly language: string }): Promise<void>;
  stop(): Promise<void>;
  cancel(): Promise<void>;
  addListener(event: "heard", listener: (event: { readonly text: string; readonly settled: boolean }) => void): Promise<Removable> | Removable;
  addListener(event: "ended", listener: (event: { readonly problem?: string }) => void): Promise<Removable> | Removable;
}

const SHELL_PLUGIN = "ArkSpeech";

const PROBLEMS: readonly DictationProblem[] = ["denied", "no-speech", "no-mic", "network", "language", "failed"];

function problemOf(code: string | undefined): DictationProblem | null {
  if (code == null || code === "") return null;
  return PROBLEMS.includes(code as DictationProblem) ? (code as DictationProblem) : "failed";
}

export function shellDictation(plugin: ShellSpeech): Dictation {
  return {
    kind: "shell",
    listensThroughPauses: false,
    start({ language }, handlers) {
      // Android hears one utterance per session: each settled result closes it, so what came before is kept here.
      let settled: string[] = [];
      let over = false;
      const listeners: Promise<Removable>[] = [];
      const finish = (problem: DictationProblem | null): void => {
        if (over) return;
        over = true;
        for (const listener of listeners) void listener.then((handle) => handle.remove()).catch(() => undefined);
        handlers.onEnd(problem);
      };
      listeners.push(
        Promise.resolve(
          plugin.addListener("heard", (event) => {
            if (over) return;
            if (event.settled) {
              settled = [...settled, event.text];
              handlers.onHeard({ settled: join(settled), guess: "" });
            } else {
              handlers.onHeard({ settled: join(settled), guess: event.text.trim() });
            }
          }),
        ),
        Promise.resolve(plugin.addListener("ended", (event) => finish(problemOf(event.problem)))),
      );
      void Promise.all(listeners)
        .then(() => plugin.start({ language }))
        .catch((error: unknown) => finish(problemOf((error as { code?: string } | null)?.code) ?? "failed"));
      return {
        stop: () => void plugin.stop().catch(() => finish(null)),
        cancel: () => {
          void plugin.cancel().catch(() => undefined);
          finish(null);
        },
      };
    },
  };
}

// --- the browser: the Web Speech API ---

interface RecognitionAlternative {
  readonly transcript: string;
}

interface RecognitionResult {
  readonly isFinal: boolean;
  readonly length: number;
  readonly [index: number]: RecognitionAlternative;
}

/** The part of SpeechRecognition used here; TypeScript's DOM library does not declare it. */
export interface Recognition {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  onresult: ((event: { readonly results: ArrayLike<RecognitionResult> }) => void) | null;
  onerror: ((event: { readonly error: string }) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

/** The Web Speech API's error names, as the composer tells them. "aborted" is our own cancel. */
function browserProblem(error: string): DictationProblem | null {
  switch (error) {
    case "aborted":
      return null;
    case "not-allowed":
    case "service-not-allowed":
      return "denied";
    case "no-speech":
      return "no-speech";
    case "audio-capture":
      return "no-mic";
    case "network":
      return "network";
    case "language-not-supported":
      return "language";
    default:
      return "failed";
  }
}

/**
 * `continuous` keeps listening through pauses until stopped, which suits a
 * longer message. Chrome on Android repeats earlier words in that mode, so
 * there each session is one utterance, ended by a pause, as in the app.
 */
export function browserDictation(Recognizer: new () => Recognition, continuous: boolean): Dictation {
  return {
    kind: "browser",
    listensThroughPauses: continuous,
    start({ language }, handlers) {
      const recognition = new Recognizer();
      recognition.lang = language;
      recognition.continuous = continuous;
      recognition.interimResults = true;
      recognition.maxAlternatives = 1;
      let problem: DictationProblem | null = null;
      let over = false;
      recognition.onresult = (event) => {
        const settled: string[] = [];
        const guess: string[] = [];
        for (let index = 0; index < event.results.length; index += 1) {
          const result = event.results[index]!;
          (result.isFinal ? settled : guess).push(result[0]?.transcript ?? "");
        }
        if (!over) handlers.onHeard({ settled: join(settled), guess: join(guess) });
      };
      recognition.onerror = (event) => {
        problem ??= browserProblem(event.error);
      };
      recognition.onend = () => {
        if (over) return;
        over = true;
        handlers.onEnd(problem);
      };
      try {
        recognition.start();
      } catch {
        over = true;
        queueMicrotask(() => handlers.onEnd("failed"));
      }
      return {
        stop: () => recognition.stop(),
        cancel: () => {
          recognition.onresult = null;
          recognition.abort();
        },
      };
    },
  };
}

// --- which one, if any ---

export interface DictationWindow {
  readonly isSecureContext?: boolean;
  readonly navigator?: { readonly userAgent?: string };
  readonly SpeechRecognition?: new () => Recognition;
  readonly webkitSpeechRecognition?: new () => Recognition;
  readonly Capacitor?: {
    readonly isNativePlatform?: () => boolean;
    readonly isPluginAvailable?: (name: string) => boolean;
    readonly Plugins?: Readonly<Record<string, unknown>>;
  };
}

/**
 * The shell's plugin when the app was built with it; otherwise the
 * browser's own, on a secure page; otherwise none, and the mic is not
 * shown. An app built before the plugin is "none": its WebView has no
 * speech recognition either. The plugin may still say the phone has no
 * recogniser (see `shellDictationAvailable`).
 */
export function findDictation(win: DictationWindow): Dictation | null {
  const bridge = win.Capacitor;
  if (bridge?.isNativePlatform?.() === true) {
    const plugin = bridge.Plugins?.[SHELL_PLUGIN];
    return bridge.isPluginAvailable?.(SHELL_PLUGIN) === true && plugin != null ? shellDictation(plugin as ShellSpeech) : null;
  }
  if (win.isSecureContext !== true) return null;
  const Recognizer = win.SpeechRecognition ?? win.webkitSpeechRecognition;
  if (Recognizer == null) return null;
  const android = /Android/i.test(win.navigator?.userAgent ?? "");
  return browserDictation(Recognizer, !android);
}

/** In the shell, whether the phone has a speech recogniser at all (a phone without Google's may not). */
export async function shellDictationAvailable(win: DictationWindow): Promise<boolean> {
  const plugin = win.Capacitor?.Plugins?.[SHELL_PLUGIN] as ShellSpeech | undefined;
  if (plugin == null) return true;
  try {
    return (await plugin.available()).available;
  } catch {
    return false;
  }
}

/** The line the composer shows when a session ended early. */
export function dictationProblemText(problem: DictationProblem): string {
  const t = text(DICTATION);
  switch (problem) {
    case "denied":
      return t.problemDenied;
    case "no-speech":
      return t.problemNoSpeech;
    case "no-mic":
      return t.problemNoMic;
    case "network":
      return t.problemNetwork;
    case "language":
      return t.problemLanguage;
    case "failed":
      return t.problemFailed;
  }
}
